import { useState, useCallback } from 'react'
import {
  IContest,
  IDisciplineSelection,
  IWeeklySchedule,
  TWeekDay,
  TKnowledgeLevel,
  TRelevanceLevel,
  ISessionDurationRange,
  TOrganizationType,
} from '../types'
import { calculateAllWeights } from '../utils/weightCalculation'

// Centralized configuration data interface
export interface IConfigurationData {
  name: string
  contest: IContest | null
  disciplines: IDisciplineSelection[]
  weeklySchedule: {
    weeklyHours: number
    schedule: IWeeklySchedule
    totalWeeklyHours: number
    selectedDays: TWeekDay[]
    sessionDurationRange: ISessionDurationRange
    organizationType: TOrganizationType
  }
}

// Configuration summary interface
export interface IConfigurationSummary {
  name: string
  contest: IContest | null
  selectedDisciplines: IDisciplineSelection[]
  disciplines: number
  totalWeeklyHours: number
  selectedDays: string[]
  weeklyHours: number
  weeklySchedule: IWeeklySchedule
  sessionDurationRange: ISessionDurationRange
  organizationType: TOrganizationType
  motivationalMessage: string
}

// API payload interface
export interface IPlanningPayload {
  name: string
  contestId: string
  type: TOrganizationType
  disciplines: Array<{
    ref: string
    knowledgeLevel: TKnowledgeLevel
    relevanceLevel: TRelevanceLevel
  }>
  weeklySchedule: {
    weeklyHours: number
    schedule: IWeeklySchedule
    sessionDurationRange: ISessionDurationRange
  }
}

export const useConfigurationData = () => {
  // Centralized state for all configuration data
  const [configurationData, setConfigurationData] = useState<IConfigurationData>({
    name: '',
    contest: null,
    disciplines: [],
    weeklySchedule: {
      weeklyHours: 20,
      schedule: {},
      totalWeeklyHours: 0,
      selectedDays: [],
      sessionDurationRange: {
        minDuration: 30, // 30 minutes default
        maxDuration: 120, // 2 hours default
      },
      organizationType: 'cycle', // Default to cycle type
    },
  })

  // Update planning name
  const updatePlanningName = useCallback((name: string) => {
    setConfigurationData((prev) => ({
      ...prev,
      name,
    }))
  }, [])

  // Update contest selection
  const updateContest = useCallback((contest: IContest | null) => {
    setConfigurationData((prev) => ({
      ...prev,
      contest,
    }))
  }, [])

  // Update disciplines data
  const updateDisciplines = useCallback((disciplines: IDisciplineSelection[]) => {
    setConfigurationData((prev) => ({
      ...prev,
      disciplines,
    }))
  }, [])

  // Update discipline selection
  const updateDisciplineSelection = useCallback((disciplineRef: string, isSelected: boolean) => {
    setConfigurationData((prev) => {
      const updated = prev.disciplines.map((d) =>
        d.ref === disciplineRef ? { ...d, isSelected } : d
      )
      return {
        ...prev,
        disciplines: calculateAllWeights(updated),
      }
    })
  }, [])

  // Update knowledge level
  const updateKnowledgeLevel = useCallback(
    (disciplineRef: string, knowledgeLevel: TKnowledgeLevel) => {
      setConfigurationData((prev) => {
        const updated = prev.disciplines.map((d) =>
          d.ref === disciplineRef ? { ...d, knowledgeLevel } : d
        )
        return {
          ...prev,
          disciplines: calculateAllWeights(updated),
        }
      })
    },
    []
  )

  // Update relevance level
  const updateRelevanceLevel = useCallback(
    (disciplineRef: string, relevanceLevel: TRelevanceLevel) => {
      setConfigurationData((prev) => {
        const updated = prev.disciplines.map((d) =>
          d.ref === disciplineRef ? { ...d, relevanceLevel } : d
        )
        return {
          ...prev,
          disciplines: calculateAllWeights(updated),
        }
      })
    },
    []
  )

  // Update weekly schedule
  const updateWeeklySchedule = useCallback(
    (weeklyData: {
      weeklyHours: number
      schedule: IWeeklySchedule
      totalWeeklyHours: number
      selectedDays: TWeekDay[]
      sessionDurationRange: ISessionDurationRange
      organizationType: TOrganizationType
    }) => {
      setConfigurationData((prev) => ({
        ...prev,
        weeklySchedule: weeklyData,
      }))
    },
    []
  )

  // Get selected disciplines
  const getSelectedDisciplines = useCallback(() => {
    const data = configurationData.disciplines.filter((d) => d.isSelected)
    const sortedByWeight = data.sort((a, b) => b.weight - a.weight)
    return sortedByWeight
  }, [configurationData.disciplines])

  // Validation functions
  const isContestSelected = useCallback(() => {
    return !!configurationData.contest
  }, [configurationData.contest])

  const isDisciplinesSelected = useCallback(() => {
    return configurationData.disciplines.some((d) => d.isSelected)
  }, [configurationData.disciplines])

  const isWeeklyScheduleConfigured = useCallback(() => {
    // For cycle type, only need weekly hours
    if (configurationData.weeklySchedule.organizationType === 'cycle') {
      return configurationData.weeklySchedule.weeklyHours > 0
    }
    // For schedule type, need both weekly hours and selected days
    return configurationData.weeklySchedule.selectedDays.length > 0
  }, [
    configurationData.weeklySchedule.selectedDays,
    configurationData.weeklySchedule.weeklyHours,
    configurationData.weeklySchedule.organizationType,
  ])

  const isConfigurationComplete = useCallback(() => {
    return (
      configurationData.name.trim() !== '' &&
      isContestSelected() &&
      isDisciplinesSelected() &&
      isWeeklyScheduleConfigured()
    )
  }, [configurationData.name, isContestSelected, isDisciplinesSelected, isWeeklyScheduleConfigured])

  // Generate motivational message
  const generateMotivationalMessage = useCallback(
    (disciplineCount: number, totalHours: number): string => {
      if (totalHours >= 20) {
        return 'Excelente! Você está se dedicando muito aos estudos. Continue assim!'
      } else if (totalHours >= 15) {
        return 'Ótima dedicação! Seu planejamento está bem estruturado.'
      } else if (totalHours >= 10) {
        return 'Boa organização! Você está no caminho certo para o sucesso.'
      } else {
        return 'Começar é o primeiro passo. Ajuste conforme sua rotina permitir.'
      }
    },
    []
  )

  // Generate configuration summary
  const getConfigurationSummary = useCallback((): IConfigurationSummary => {
    const selectedDisciplines = getSelectedDisciplines()

    return {
      name: configurationData.name,
      contest: configurationData.contest,
      selectedDisciplines,
      totalWeeklyHours: configurationData.weeklySchedule.totalWeeklyHours,
      selectedDays: configurationData.weeklySchedule.selectedDays,
      weeklyHours: configurationData.weeklySchedule.weeklyHours,
      weeklySchedule: configurationData.weeklySchedule.schedule,
      sessionDurationRange: configurationData.weeklySchedule.sessionDurationRange,
      organizationType: configurationData.weeklySchedule.organizationType,
      disciplines: configurationData.disciplines.length,
      motivationalMessage: generateMotivationalMessage(
        selectedDisciplines.length,
        configurationData.weeklySchedule.totalWeeklyHours
      ),
    }
  }, [configurationData, getSelectedDisciplines, generateMotivationalMessage])

  // Prepare payload for API submission
  const getPlanningPayload = useCallback((): IPlanningPayload => {
    if (!isConfigurationComplete()) {
      throw new Error('Configuration is incomplete')
    }

    return {
      name: configurationData.name.trim(),
      contestId: configurationData.contest!._id,
      type: configurationData.weeklySchedule.organizationType,
      disciplines: getSelectedDisciplines().map((d) => ({
        ref: d.ref,
        knowledgeLevel: d.knowledgeLevel,
        relevanceLevel: d.relevanceLevel,
      })),
      weeklySchedule: {
        weeklyHours: configurationData.weeklySchedule.weeklyHours,
        schedule: configurationData.weeklySchedule.schedule,
        sessionDurationRange: configurationData.weeklySchedule.sessionDurationRange,
      },
    }
  }, [configurationData, isConfigurationComplete, getSelectedDisciplines])

  return {
    // State
    configurationData,

    // Update functions
    updatePlanningName,
    updateContest,
    updateDisciplines,
    updateDisciplineSelection,
    updateKnowledgeLevel,
    updateRelevanceLevel,
    updateWeeklySchedule,

    // Getter functions
    getSelectedDisciplines,
    getConfigurationSummary,
    getPlanningPayload,

    // Validation functions
    isContestSelected,
    isDisciplinesSelected,
    isWeeklyScheduleConfigured,
    isConfigurationComplete,
  }
}
